// SF6 — Revenue by Category and Brand.
//
// One component with a switch between the two grouping keys, not two tables.
//
// Rows carry no image and no ASIN: a group is not a product. The ASIN count is
// the column that makes the table readable, because it separates a large
// category from one high-value product.
//
// Both keys resolve from Amazon itself: brand is a listing attribute, and
// category is Amazon's own browse classification, which arrives in the same
// call. Neither needs a mapping table anyone has to author and maintain.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FiAlertTriangle, FiInfo } from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../SalesPerformance.module.css';
import { Change, ClippedWindow, count, money } from './format';
import { getRevenueByGroup } from '../services/salesPerformanceApi';

const COLUMNS = ['Rank', 'Category / Brand', 'ASINs', 'Sales', 'Units', 'Growth %'];

const RevenueByGroup = ({ window: reportingWindow, onLoadingChange }) => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;
  const { start, end } = reportingWindow || {};

  const [groupBy, setGroupBy] = useState('brand');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token || !start || !end) return;
    setLoading(true);
    onLoadingChange?.(true);
    setError(null);
    try {
      setData(await getRevenueByGroup(token, { start, end, groupBy }));
    } catch (err) {
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  }, [token, start, end, groupBy, onLoadingChange]);

  useEffect(() => { load(); }, [load]);

  // Unmounting mid-load would otherwise leave the page's Apply button
  // stuck reporting this component as still running.
  useEffect(() => () => onLoadingChange?.(false), [onLoadingChange]);

  const rows = data?.rows || [];
  const groupings = data?.groupings || [
    { key: 'brand', label: 'Brand', available: true },
    { key: 'category', label: 'Category', available: true },
  ];

  return (
    <section className={local.card}>
      <div className={local.cardHead}>
        <h2 className={local.cardTitle}>Revenue by category and brand</h2>
        <div className={local.headAside}>
          <div className={local.segmented}>
            {groupings.map((option) => (
              <button
                key={option.key}
                type="button"
                title={option.available ? undefined : option.reason}
                className={option.key === groupBy ? local.segmentOn : local.segment}
                onClick={() => setGroupBy(option.key)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <p className={local.muted}>
        Revenue above the product level. The ASIN count separates a large group
        from a single high-value product, and counts variation families once.
      </p>
      <ClippedWindow data={data} />

      {error && (
        <div className={`${local.banner} ${error.reportUnavailable ? local.bannerWarn : local.bannerError}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div>
            <strong>
              {error.notConnected ? 'No Amazon account connected'
                : error.reportUnavailable ? 'Data not ready' : 'Could not load the breakdown'}
            </strong>
            <div>{error.message}</div>
          </div>
        </div>
      )}

      {!error && (
        <div className={local.tableScroll}>
          <table className={local.table}>
            <thead>
              <tr>{COLUMNS.map((column) => <th key={column}>{column}</th>)}</tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td className={local.emptyCell} colSpan={COLUMNS.length}>
                    {loading ? 'Loading…' : 'No sales in this period.'}
                  </td>
                </tr>
              ) : rows.map((row) => (
                <tr key={row.group}>
                  <td className={local.rankCell}>{row.rank}</td>
                  <td>
                    <span className={row.group === data?.ungrouped_label
                      ? local.groupUnknown : local.groupName}>
                      {row.group}
                    </span>
                  </td>
                  <td className={local.numeric}>{count(row.asin_count)}</td>
                  <td className={local.numeric}>{money(row.revenue)}</td>
                  <td className={local.numeric}>{count(row.units)}</td>
                  <td className={local.numeric}><Change value={row.growth} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data?.notes?.length > 0 && (
        <ul className={local.noteList}>
          {data.notes.map((note) => (
            <li key={note}><FiInfo className={local.noteIcon} />{note}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default RevenueByGroup;
